import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./lib/zodConfig";
import App from "./App.tsx";
// Self-hosted Montserrat (normal + italic, weights 100-900). Vite bundles the
// woff2 files, so no request goes to Google Fonts (privacy fact sheet F6).
import "@fontsource-variable/montserrat/wght.css";
import "@fontsource-variable/montserrat/wght-italic.css";
import "./styles/index.css";
import { LocalizedClerkProvider } from "./auth/LocalizedClerkProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import { createAppQueryClient } from "./lib/queryClient";
import { BrowserRouter } from "react-router";
import { ThemeProvider } from "./contexts/ThemeContext";
import { I18nProvider } from "./contexts/I18nProvider";
import { SentryFallback } from "./components/SentryFallback";
import { ScrollToTop } from "./components/ScrollToTop";
import { configureDevBypassApiHeaders } from "./lib/devBypass";
import { subscribeToMoneyViewInvalidations } from "./hooks/reconciliation/invalidateMoneyViews";
import {
  FEEDBACK_IDENTITY_OPTIONS,
  scrubSentryEvent,
  scrubSentryLog,
  scrubSentryTransaction,
} from "./lib/sentryScrub";
import { isMePath, meSentryReplayRates } from "./components/me/mePath";

// Initialize Sentry as early as possible
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const appEnv = import.meta.env.VITE_APP_ENV || "development";
const appVersion = import.meta.env.VITE_APP_VERSION || "dev";
const replayRates = meSentryReplayRates(window.location.pathname, appEnv);

let sentryInitialized = false;

if (sentryDsn) {
  try {
    Sentry.init({
      dsn: sentryDsn,
      environment: appEnv,
      release: appVersion,
      sendDefaultPii: false,
      beforeSend: scrubSentryEvent,
      beforeSendTransaction: scrubSentryTransaction,

      // Enable experimental features
      _experiments: {
        enableLogs: true,
        // Log records skip `beforeSend`; this hook applies the same scrub rules (#1878).
        beforeSendLog: scrubSentryLog,
      },

      integrations: [
        // React-specific integrations
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
          // The /me portal (#1768) is never recorded. A replay started on an
          // admin page keeps running across the SPA navigation to /me until
          // useSuspendSentryReplay stops it at the first commit; this drops
          // the custom recording events (navigation URLs, breadcrumbs, network
          // spans) captured on /me in that window. The SDK does not run this
          // callback for rrweb DOM events; those stay masked by the two
          // options above.
          beforeAddRecordingEvent: (event) =>
            isMePath(window.location.pathname) ? null : event,
          // An error on /me never promotes a buffered replay to a sent one.
          beforeErrorSampling: () => !isMePath(window.location.pathname),
        }),
        // User feedback widget (with custom button)
        Sentry.feedbackIntegration({
          autoInject: false, // We'll use our own button
          showBranding: false,
          colorScheme: "light", // Default to light, will be overridden by FeedbackButton
          formTitle: "Send Feedback",
          submitButtonLabel: "Send",
          successMessageText: "Thank you for your feedback!",
          ...FEEDBACK_IDENTITY_OPTIONS,
          // Theme colors to match our application
          themeLight: {
            fontFamily: "font-montserrat",
            background: "#fafafa", // hsl(0 0% 98%) - sidebar background
            foreground: "#3f3f46", // hsl(240 5.3% 26.1%) - sidebar foreground
            backgroundHover: "#f5f5f6", // hsl(240 4.8% 95.9%) - sidebar accent
            //border: "1px solid #1c1917", // hsl(20 5.9% 90%) - border
            //outline: "1px solid #facc15", // hsl(47.9 95.8% 53.1%) - primary color
            submitBackground: "#facc15", // hsl(47.9 95.8% 53.1%) - primary color
            submitBackgroundHover: "#facc15",
            submitForeground: "#422006", // hsl(26 83.3% 14.1%) - primary-foreground
            cancelBackground: "#f5f5f4", // hsl(60 4.8% 95.9%) - secondary
            cancelForeground: "#1c1917", // hsl(24 9.8% 10%) - secondary-foreground
            inputBackground: "#fafafa", // hsl(0 0% 98%) for input fields
            inputBorder: "1px solid #e5e5e5", // hsl(20 5.9% 90%) - border color
            successColor: "#166534", // dark green text on light background
          },
          themeDark: {
            fontFamily: "font-montserrat",
            background: "#18181b", // hsl(240 5.9% 10%) - dark sidebar background
            foreground: "#f5f5f4", // hsl(240 4.8% 95.9%) - dark sidebar foreground
            backgroundHover: "#27272a", // hsl(240 3.7% 15.9%) - dark sidebar accent
            //border: "1px solid #facc15", // Lighter border for visibility in dark mode
            //outline: "1px solid #facc15", // hsl(47.9 95.8% 53.1%) - primary color
            submitBackground: "#facc15", // hsl(47.9 95.8% 53.1%) - primary color
            submitBackgroundHover: "#facc15",
            submitForeground: "#422006", // hsl(26 83.3% 14.1%) - primary-foreground
            cancelBackground: "#27272a", // hsl(240 3.7% 15.9%) - dark sidebar accent
            cancelForeground: "#f5f5f4", // hsl(240 4.8% 95.9%) - dark sidebar foreground
            inputBackground: "#0A0909", // dark background for input fields
            inputBorder: "1px solid #4a4a4d", // Lighter border for visibility
            successColor: "#4ade80", // bright green text on dark background
          },
        }),
      ],

      // Performance monitoring. Production keeps a 10% trace sample; outside
      // production we disable trace sampling so local profiling/perf work isn't
      // distorted by Sentry instrumentation overhead.
      tracesSampleRate: appEnv === "production" ? 0.1 : 0,

      // Session replay. Production keeps a 10% session-replay sample; outside
      // production session replay is off (errors still capture a replay below).
      // The /me self-service portal (#1768) never records a replay: both rates
      // are 0 when the page loads on /me (SPA entry: useSuspendSentryReplay).
      replaysSessionSampleRate: replayRates.session,
      replaysOnErrorSampleRate: replayRates.onError,
    });

    sentryInitialized = true;
  } catch (error) {
    console.error("❌ Failed to initialize Sentry:", error);
    console.warn(
      "Sentry error reporting will be disabled. This may be due to ad blockers or network issues."
    );
  }
} else {
  console.log("⚠️ VITE_SENTRY_DSN not set, skipping Sentry initialization");
}

// Export Sentry status for use in the app
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__SENTRY_INITIALIZED__ = sentryInitialized;

// Check for development auth bypass
const isDev = import.meta.env.DEV;
const bypassAuth = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

// Only require Clerk publishable key if not bypassing auth
// When bypass is active, the @clerk/clerk-react import is redirected to our mock via Vite alias
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!bypassAuth && !PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

// Warn if bypass is active
if (bypassAuth && isDev) {
  console.warn('⚠️  AUTH BYPASS ACTIVE - Development mode only');
  console.warn('All @clerk/clerk-react imports redirected to mock module via Vite alias');
}

// Security review P10b: when the mock-Clerk bypass is active, every API request
// must carry the X-Dev-Bypass-Secret header (the backend's User-Agent gate is
// gone). This wires the header onto the generated OpenAPI client and the
// hand-written fetch hooks. No-op (and excluded) in production builds.
configureDevBypassApiHeaders();

const queryClient = createAppQueryClient();

// Cross-tab money invalidation. A QueryClient is per-tab, so a correction made
// in one tab cannot reach the caches of another; this listens for the
// invalidation another tab broadcasts and runs the same pass here. Subscribed
// for the lifetime of the document — it touches only the money namespaces, and
// there is nothing to tear down before the tab goes away.
subscribeToMoneyViewInvalidations(queryClient);

// Disable StrictMode in development when using Clerk to prevent double-provider errors
const useStrictMode = !isDev;

const app = (
  <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
    <BrowserRouter>
      <ScrollToTop />
      <I18nProvider>
        <LocalizedClerkProvider publishableKey={PUBLISHABLE_KEY || 'mock'}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </QueryClientProvider>
        </LocalizedClerkProvider>
      </I18nProvider>
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);

ReactDOM.createRoot(document.getElementById("root")!).render(
  useStrictMode ? <React.StrictMode>{app}</React.StrictMode> : app
);

// Export statement to satisfy React Fast Refresh requirements
export {};
